<style lang="scss">
  @use "@sourcewhale/shared/svelte-design-system/assets/css/design-system/colors";
  @use "@sourcewhale/shared/svelte-design-system/assets/css/design-system/utils";
  @use "@sourcewhale/shared/svelte-design-system/assets/css/design-system/mixins";

  .header {
    display: flex;
    align-items: center;
    gap: utils.in_rem(12px);
    padding: 0 utils.in_rem(8px);
  }

  .body {
    display: flex;
    flex-direction: column;
    gap: utils.in_rem(8px);
    padding: utils.in_rem(2px) utils.in_rem(24px) utils.in_rem(24px)
      utils.in_rem(24px);
    width: utils.in_rem(600px);

    p {
      @include mixins.body2();
    }
  }

  .footer {
    display: flex;
    justify-content: flex-end;
    gap: utils.in_rem(16px);
    padding: utils.in_rem(12px) 0;
    margin: 0 utils.in_rem(24px);
    border-top: 1px solid colors.get(interfaceSwGrey_10);
  }
</style>

<script>
  // Svelte
  import { getContext } from "svelte";
  // Functions
  import { showToast } from "@sourcewhale/core";
  import { sendSms } from "../../../../../api/sms";
  import { sendWhatsApp } from "../../../../../api/whatsapp";
  // Components
  import Button from "@sourcewhale/shared/svelte-design-system/components/atoms/Button.svelte";
  import Icon from "@sourcewhale/shared/svelte-design-system/components/atoms/Icon.svelte";
  import InlineNotification from "@sourcewhale/shared/svelte-design-system/components/atoms/InlineNotification.svelte";
  import TextInputWithCounter from "@sourcewhale/shared/svelte-design-system/components/molecules/InputFields/TextInputWithCounter.svelte";
  import Modal from "@sourcewhale/shared/svelte-design-system/components/molecules/Modal/Modal.svelte";
  // Context
  import { CONTACT_CTAS_CONTEXT } from "../context";

  // Get store from context
  const { activePopUpModalStore, activityFeedStore, candidateStore } =
    getContext(CONTACT_CTAS_CONTEXT);

  export let kind = ""; // Service kind - "whatsapp" or "sms"
  export let number = ""; // Recipient's number

  const kindMapping = {
    whatsapp: {
      title: "WhatsApp",
      icon: "fa-brands fa-whatsapp",
      limit: 5000,
      function: sendWhatsApp,
    },
    sms: {
      title: "SMS",
      icon: "fa-regular fa-sms",
      limit: 1500,
      function: sendSms,
    },
  };

  let message = "";
  // Dynamic states - check if message is over the limit and if it can be sent
  $: isOverLimit = message.length > kindMapping[kind].limit;
  $: canSend = message.length > 0 && !isOverLimit;

  const confirm = () => {
    activePopUpModalStore.set(null);
    activityFeedStore.get(); // Refresh the activity feed to see new message
  };

  const cancel = () => {
    activePopUpModalStore.set(null);
  };

  const wrappedConfirm = () => {
    const body = { candidateId: $candidateStore.candidateId, message, number };
    return kindMapping[kind].function(body).then(() => {
      showToast({
        title: `Success!`,
        subtitle: `${kindMapping[kind].title} message sent to ${number}`,
        type: "success",
      });
      confirm();
    });
  };
</script>

<Modal onclose={cancel} blur="4px">
  <div class="header" slot="header">
    <Icon class={kindMapping[kind].icon} size="large" />
    <h2>{kindMapping[kind].title} message {number}</h2>
  </div>
  <div class="body">
    {#if isOverLimit}
      <InlineNotification message="warning">
        <p>
          Your message exceeds the {kindMapping[kind].limit} character limit. Please
          shorten it to proceed
        </p>
      </InlineNotification>
    {/if}
    <TextInputWithCounter
      kind="textarea"
      placeholder="Write an {kindMapping[kind].title} message..."
      bind:value={message}
      showCounter
      characterLimit={kindMapping[kind].limit}
      autoFocus={true}
    />
    {#if kind === "whatsapp"}
      <InlineNotification>
        <p>
          Please note: you will only need to send the first message via
          SourceWhale, and then we will continue to track all future messages in
          SourceWhale and your CRM from all devices for this specific contact.
        </p>
      </InlineNotification>
    {/if}
  </div>
  <div class="footer">
    <Button class="flex-1" onclick={cancel} kind="ghost-blue">Cancel</Button>
    <Button
      class="flex-1 {canSend ? '' : 'disabled'}"
      onclick={() => (canSend ? wrappedConfirm() : null)}>Send</Button
    >
  </div>
</Modal>
